import { useState, useCallback, useEffect } from 'react';
import { Form } from 'antd';
import { useApplicationId } from '../../../hooks/useApplicationId';
import {
	Application,
	FeedbackWithConfirmation,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useCallApi } from '../../../hooks/useCallApi';
import {
	submitProposerRecommendation,
	skipFeedbackCollectionByProposer,
} from '../../../service/VolunteerApplicationService';
import { useAuth } from '../../../hooks/useAuth';
import { FUNCTIONARY_HOME, INTERVIEW_LANDING } from '../../../shared/Routes';
import { ReviewAction } from '../../../shared/Constants';
import { useConfirmPopup } from '../../../hooks/useConfirmPopup';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import useUserSession from '../../../hooks/useUserSession';
import { resetApplicationsListCache } from '../ApplicationsList/useApplicationsList';

interface UseProposerRecommendationPageConfig {
	isSkipInterview?: boolean;
}

export const useProposerRecommendationPage = ({
	isSkipInterview = false,
}: UseProposerRecommendationPageConfig) => {
	const [recommendation, setRecommendation] =
		useState<FeedbackWithConfirmation>({});
	const [submitButtonDisabled, setSubmitButtonDisabled] =
		useState<boolean>(false);
	const { confirmPopUpAction } = useConfirmPopup();
	const { infoPopUpAction } = useInfoPopup();
	const [form] = Form.useForm<FeedbackWithConfirmation>();
	const values = Form.useWatch<FeedbackWithConfirmation>([], form);
	const { fetchApplication, applicationId, navigateWithApplicationId } =
		useApplicationId();
	const [application, setApplication] = useState<Application | undefined>();
	const [isApprover, setIsApprover] = useState<boolean | undefined>(false);
	const { callApi } = useCallApi();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();

	const onConfirmSubmitForSkipAllInterviews = useCallback(async () => {
		try {
			await callApi(
				() =>
					skipFeedbackCollectionByProposer({
						recommendation,
						applicationId,
						functionarySrcmId: srcmId as string,
						selectedRoleCode: selectedRole as UserRole,
					}),
				'errorOccurredWhileSubmitting'
			);
			resetApplicationsListCache();
			infoPopUpAction(
				'submittedSuccessfully',
				'',
				() => {
					navigateWithApplicationId(FUNCTIONARY_HOME);
				},
				ConfirmTick
			);
		} catch (r) {
			// Do Nothing
		}
	}, [
		callApi,
		infoPopUpAction,
		recommendation,
		applicationId,
		srcmId,
		selectedRole,
		navigateWithApplicationId,
	]);

	const onSubmitClickForSkipAllInterviews = useCallback(() => {
		confirmPopUpAction(
			'confirmSubmit',
			'onceSubmittedFeedbackCannotBeChanged',
			onConfirmSubmitForSkipAllInterviews
		);
	}, [confirmPopUpAction, onConfirmSubmitForSkipAllInterviews]);

	const onConfirmSubmit = useCallback(async () => {
		try {
			await callApi(
				() =>
					submitProposerRecommendation({
						recommendation,
						applicationId,
						functionarySrcmId: srcmId as string,
						selectedRoleCode: selectedRole as UserRole,
					}),
				'errorOccurredWhileSubmitting'
			);
			resetApplicationsListCache();
			infoPopUpAction(
				'submittedSuccessfully',
				'',
				() => {
					navigateWithApplicationId(FUNCTIONARY_HOME);
				},
				ConfirmTick
			);
		} catch (r) {
			// Do Nothing
		}
	}, [
		callApi,
		infoPopUpAction,
		recommendation,
		applicationId,
		srcmId,
		selectedRole,
		navigateWithApplicationId,
	]);

	const onSubmitClick = useCallback(() => {
		confirmPopUpAction(
			'confirmSubmit',
			'onceSubmittedFeedbackCannotBeChanged',
			onConfirmSubmit
		);
	}, [confirmPopUpAction, onConfirmSubmit]);

	const onCancelClick = useCallback(() => {
		navigateWithApplicationId(INTERVIEW_LANDING);
	}, [navigateWithApplicationId]);

	const handleFormValuesChange = useCallback(async () => {
		try {
			const fieldValues = await form.validateFields({ validateOnly: true });
			setRecommendation(fieldValues);
			if (
				(fieldValues.review === ReviewAction.REJECT &&
					!fieldValues.predefinedReasons?.length) ||
				(!isSkipInterview && !fieldValues.videoCallCheckbox)
			) {
				setSubmitButtonDisabled(true);
			} else {
				setSubmitButtonDisabled(false);
			}
		} catch (e) {
			const fields = e as { values: FeedbackWithConfirmation };
			setRecommendation(fields.values);
			setSubmitButtonDisabled(true);
		}
	}, [form, isSkipInterview]);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, values]);

	const loadApplication = useCallback(async () => {
		try {
			const fetchedApplication: Application = await fetchApplication();
			setRecommendation(fetchedApplication?.proposerRecommendation);
			setIsApprover(fetchedApplication?.isApprover);
			setApplication(fetchedApplication);

			form.setFieldsValue(fetchedApplication?.proposerRecommendation);
		} catch (e) {
			//
		}
	}, [fetchApplication, form]);

	useEffect(() => {
		void loadApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form]);

	return {
		form,
		application,
		submitButtonDisabled,
		onSubmitClick,
		onSubmitClickForSkipAllInterviews,
		onCancelClick,
		isApprover,
	};
};
